import { z } from 'zod';
import { narrativeBlockSchema } from './NarrativeBlock';
import {
  packetCustomSectionSchema,
  sectionKeySchema,
  ToggleHiddenSectionDTO,
} from './PacketCustomSection';
import { Comment } from '../comments';
import { Sprint } from '../ad-builder/Sprint';
import { SavedAudience } from '../ad-builder/audience/SavedAudience';
import { SprintPacketSurveyStatus } from './SprintPacketSurvey';

export const sprintPacketStatusSchema = z.enum([
  'draft',
  'ready',
  'presented',
  'archived',
]);

export type SprintPacketStatus = z.infer<typeof sprintPacketStatusSchema>;

const isoDateTimeStringSchema = z
  .string()
  .refine(
    (value) =>
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(
        value,
      ) && !Number.isNaN(new Date(value).getTime()),
    'Invalid ISO datetime string',
  );

export const sentimentScaleSchema = z.enum(['yes', 'maybe', 'no']).nullable();
export type SentimentScale = z.infer<typeof sentimentScaleSchema>;

export const competitorsPositionSchema = z
  .enum(['below', 'equal', 'above'])
  .nullable();
export type CompetitorsPosition = z.infer<typeof competitorsPositionSchema>;

export const clientSentimentSchema = z.object({
  likedToday: z.string().nullable(),
  competitorsPosition: competitorsPositionSchema,
  competitorsNotes: z.string().nullable(),
  wouldRenew: sentimentScaleSchema,
  wouldRenewNotes: z.string().nullable(),
  wouldRecommend: sentimentScaleSchema,
  wouldRecommendNotes: z.string().nullable(),
  updatedAt: z.string(),
});

export type ClientSentiment = z.infer<typeof clientSentimentSchema>;

// AE-curated set of the business's saved audiences featured in this packet,
// with one designated primary. Audiences attach to a sprint only transitively
// through published campaigns, which usually don't exist at packet time, so the
// packet owns this selection rather than deriving it. It references real
// SavedAudience objects (by id) — it is not an audience-approval surface.
export const audienceSelectionSchema = z.object({
  audienceIDs: z.array(z.number()),
  primaryAudienceID: z.number().nullable(),
});

export type AudienceSelection = z.infer<typeof audienceSelectionSchema>;

// Packet-owned brand kit for the Brand Standards slide (v1.6). Flexible ordered
// lists — first font is implicitly primary. `logos` holds private S3 object keys
// (there is no public bucket); hydration signs them into short-lived URLs on
// `SprintPacketHydrated.brandLogos`. Copied forward from the business's most
// recent packet on creation, so per-packet copies preserve history for free.
export const brandStandardsSchema = z.object({
  logos: z.array(z.string()),
  fonts: z.array(z.string()),
  colors: z.array(z.string()),
});

export type BrandStandards = z.infer<typeof brandStandardsSchema>;

// A stored logo key resolved into a displayable (signed, expiring) URL.
export interface BrandLogo {
  key: string;
  url: string;
}

export const sprintPacketSchema = z.object({
  id: z.number(),
  businessID: z.number(),
  sprintID: z.number(),
  status: sprintPacketStatusSchema,
  narrative: z.array(narrativeBlockSchema).default([]),
  // AE-authored slides, ordered within each `afterKey` gap. Rides the packet
  // wire (not a second read contract) so both the list page — which counts
  // unreviewed slides per row — and the hydrated payload carry it with no
  // extra query. Written only through the dedicated custom-section routes, so
  // it is deliberately absent from UpdateSprintPacketDTO.
  customSections: z.array(packetCustomSectionSchema).default([]),
  // Slides the AE has hidden from the client, built-in or custom (slice 12.5).
  // Carries forward to next sprint's packet, so "hide Brand Standards" is a
  // decision made once. Written only one key at a time through
  // `hiddenSection` on the update DTO — deliberately absent from the `.pick()`
  // below so no client can replace the whole array.
  hiddenSections: z.array(sectionKeySchema).default([]),
  clientSentiment: clientSentimentSchema.nullish(),
  audienceSelection: audienceSelectionSchema.nullish(),
  brandStandards: brandStandardsSchema.nullish(),
  createdByUserID: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  presentedAt: isoDateTimeStringSchema.nullish(),
});

export type SprintPacket = z.infer<typeof sprintPacketSchema>;

export const CreateSprintPacketDTO = sprintPacketSchema.pick({
  sprintID: true,
});

export type CreateSprintPacket = z.infer<typeof CreateSprintPacketDTO>;

// Per-block patch: server merges by sectionKey using updatedAt as a
// tiebreaker, so two sections autosaving in parallel can't clobber each other.
// The full `narrative` array is still accepted for bulk operations (initial
// load, admin tooling), but interactive editors should send `narrativeBlock`.
export const narrativeBlockPatchSchema = narrativeBlockSchema.extend({
  updatedAt: z.string(),
});
export type NarrativeBlockPatch = z.infer<typeof narrativeBlockPatchSchema>;

// Shallow per-field merge for clientSentiment. Each entry carries its own
// updatedAt; the server keeps the newer value field-by-field, so out-of-order
// saves of different fields can't clobber each other.
export const clientSentimentPatchSchema = clientSentimentSchema
  .partial()
  .extend({
    updatedAt: z.string(),
  });
export type ClientSentimentPatch = z.infer<typeof clientSentimentPatchSchema>;

export const UpdateSprintPacketDTO = sprintPacketSchema
  .pick({
    status: true,
    presentedAt: true,
    narrative: true,
    clientSentiment: true,
    audienceSelection: true,
    brandStandards: true,
  })
  .partial()
  .extend({
    narrativeBlock: narrativeBlockPatchSchema.optional(),
    clientSentimentPatch: clientSentimentPatchSchema.optional(),
    hiddenSection: ToggleHiddenSectionDTO.optional(),
  });

export type UpdateSprintPacket = z.infer<typeof UpdateSprintPacketDTO>;

export interface SprintPacketPerformance {
  spend?: number;
  cpa?: number;
  conversions?: number;
  budget?: number;
  budgetTarget?: number;
}

export interface SprintPacketHydrated extends SprintPacket {
  sprint: Sprint;
  performance: SprintPacketPerformance | null;
  comments: Comment[];
  // The selected saved audiences, resolved server-side (business-scoped,
  // non-deleted) and ordered to match `audienceSelection.audienceIDs`. The
  // hydrated `audienceSelection` is sanitized to match these rows.
  audiences: SavedAudience[];
  // Stored brand-logo keys signed into short-lived display URLs, ordered to
  // match `brandStandards.logos`.
  brandLogos: BrandLogo[];
  // The packet's Salesforce survey link, snapshotted at create. `null` hides the
  // survey slide in Present.
  survey: SprintPacketSurveyStatus;
}
